import { useQuery } from '@tanstack/react-query';
import { Action, Card, Icon, Menu, Page } from 'corso-admin-ui';
import { isTruthy } from 'corso-types';
import { formatter } from 'corso-web-lib';
import api from '~/api';
import PageStatus from '~/components/PageStatus';
import { useStoreId } from '~/providers/StoreUserProvider';

const useQueryKey = () => {
  const storeId = useStoreId();
  return ['invoices', storeId] as const;
};

export const useInvoices = () => {
  const queryKey = useQueryKey();

  return useQuery({
    queryKey,
    queryFn: ({ queryKey: [, storeId] }) => api.store(storeId).invoices.list(),
    select: (data) =>
      data.map((invoice) => ({
        ...invoice,
        hasDownloads: [
          invoice.ordersCsvUrl,
          invoice.refundsCsvUrl,
          invoice.reordersCsvUrl,
          invoice.adjustmentsCsvUrl,
        ].some(isTruthy),
      })),
  });
};

export default function InvoicesPage() {
  const { data: invoices, isPending, isError, error } = useInvoices();

  if (isError) return <PageStatus.Error error={error} />;
  if (isPending) return <PageStatus.Loading />;

  return (
    <Page title="Invoices">
      {invoices.length ? (
        <Card>
          <ul className="divide-y divide-neutral-100">
            {invoices.map((invoice) => (
              <li
                key={invoice.id}
                className="flex items-center justify-between gap-x-6 p-4 first:rounded-t-md last:rounded-b-md even:bg-neutral-50 sm:px-6"
              >
                <div className="min-w-0">
                  <div className="flex items-start gap-x-3">
                    <p className="text-sm leading-6 font-semibold text-neutral-900">
                      Invoice #{invoice.id}
                    </p>
                  </div>
                  <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-neutral-500">
                    <p className="whitespace-nowrap">
                      Generated on{' '}
                      <time dateTime={invoice.createdOn}>
                        {formatter.date(invoice.createdOn)}
                      </time>
                    </p>
                    <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                      <circle r={1} cx={1} cy={1} />
                    </svg>
                    <p className="truncate">
                      {formatter.date(new Date(invoice.fromDate))}&nbsp;-&nbsp;
                      {formatter.date(new Date(invoice.toDate))}
                    </p>
                  </div>
                </div>
                <div className="flex flex-none items-center gap-x-4">
                  {invoice.isMonthlyCrew && (
                    <p className="text-sm leading-6 font-semibold text-neutral-900">
                      {formatter.currency(invoice.totalAmount, 'USD')}
                    </p>
                  )}
                  {invoice.invoicePdfUrl && (
                    <Action
                      icon="downloadFile"
                      accessibilityLabel="Download PDF"
                      to={invoice.invoicePdfUrl}
                    />
                  )}
                  {invoice.hasDownloads && (
                    <Menu
                      buttonAs={
                        <Action
                          icon="overflowMenuHorizontal"
                          accessibilityLabel="CSV Downloads"
                        />
                      }
                    >
                      {invoice.ordersCsvUrl && (
                        <Menu.ItemLink to={invoice.ordersCsvUrl}>
                          <Icon icon="downloadFile" className="size-4" />
                          Orders
                        </Menu.ItemLink>
                      )}
                      {invoice.refundsCsvUrl && (
                        <Menu.ItemLink to={invoice.refundsCsvUrl}>
                          <Icon icon="downloadFile" className="size-4" />
                          Refunds
                        </Menu.ItemLink>
                      )}
                      {invoice.reordersCsvUrl && (
                        <Menu.ItemLink to={invoice.reordersCsvUrl}>
                          <Icon icon="downloadFile" className="size-4" />
                          Reorders
                        </Menu.ItemLink>
                      )}
                      {invoice.adjustmentsCsvUrl && (
                        <Menu.ItemLink to={invoice.adjustmentsCsvUrl}>
                          <Icon icon="downloadFile" className="size-4" />
                          Adjustments
                        </Menu.ItemLink>
                      )}
                    </Menu>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : (
        <PageStatus
          icon="receipt"
          title="No Invoices Found"
          detail="No invoices have been generated, please check back later."
        />
      )}
    </Page>
  );
}
